import React, { useContext, useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../../theming'
import Button, { PrimaryCta } from '../Button'
import * as AboutPage from '../About'
import AppContext from '../../helpers/AppContext'
import ImagePicker from '../ImagePicker'
import Image from '../Image'
import TextLine from '../TextLine'
import { getTagging, ImageTaggingResponse } from '../../helpers/bacillusClient'
import axios from 'axios'

export interface Props {
    className?: string
    primary?: boolean
}

async function srcToBlob (src: string) {
    const response = await axios({
        method: 'get',
        url: src,
        responseType: 'blob'
    })

    return response.data
}

const BackgroundImgRemover: React.FC<Props> = (props) => {
    const { className } = props
    const ctx = useContext(AppContext)
    const [gettingTags, setGettingTags] = useState(false)
    const [imgSrc, setImgSrc] = useState<string>()
    const [tagsResponse, setTagsResponse] = useState<ImageTaggingResponse>()

    useEffect(() => {
        if (!imgSrc) {
            return
        }

        ctx.openDrawer(getImgViewer(imgSrc))
    }, [imgSrc, gettingTags, tagsResponse])

    const getImgViewer = (src: string) => {
        let viewerFooter = (
            <div style={{marginTop: '10px'}}>
                <PrimaryCta icon="Check" full={true} onClick={getTags} />
            </div>
        )
        if (gettingTags) {
            viewerFooter = (
                <>
                    <TextLine
                        text="Fetching Tags ..."
                        type="subtitle"
                        center={true}
                    />
                </>
            )
        } else if (tagsResponse) {
            viewerFooter = (
                <div style={{marginTop: '10px', marginBottom: '10px'}}>
                    <TextLine text="Colors" type="subtitle"/>
                    {tagsResponse.colors.map((color) => (
                        <div style={{background: `#${color.code}`, width: '20px', height: '20px', marginRight: '10px', display: 'inline-block'}}></div>
                    ))}
                    <TextLine text="Features" type="subtitle"/>
                    {tagsResponse.keywords.map((keyword) => (
                        <div style={{marginBottom: '10px'}}>
                            <TextLine text={`${keyword.classification}:`} type="caption" inline={true} bold={true}/>
                            &nbsp;&nbsp;&nbsp;
                            <TextLine text={keyword.name} type="caption" inline={true}/>
                        </div>
                    ))}
                </div>
            )
        }

        return (
            <div>
                <div style={{ height: tagsResponse ? '20vh' : '60vh' }}>
                    <Image
                        className="expandedQueryImage"
                        height="100%"
                        width="100%"
                        src={src}
                        alt="query image"
                        fit="contain"
                        setCors={false}
                    />
                </div>
                {viewerFooter}
            </div>
        )
    }

    const getTags = async () => {
        if (!imgSrc) {
            return
        }

        setGettingTags(true)
        const imgBlob = await srcToBlob(imgSrc)
        try {
            const resp = await getTagging(imgBlob)
            setTagsResponse(resp)
        } catch (error) {
            ctx.setToastMessage({
                text: 'Request failed because image is too large or format is unsupported',
                type: 'error',
                timeout: 7000
            })
        } finally {
            setGettingTags(false)
        }
    }

    const onImageAdded = async (src: string) => {
        setImgSrc(src)
        setTagsResponse(undefined)
    }

    return (
        <div className={className}>
            <div className="container">
                <div className="display">
                    <h1 className={'hero-text'}>
                        <AboutPage.MarkedText>
                            <span>Detect</span>
                        </AboutPage.MarkedText>
                        <span>Apparel Image </span>
                        <AboutPage.MarkedText>
                            <span>Properties</span>
                        </AboutPage.MarkedText>
                    </h1>
                </div>
                <ImagePicker onImageAdded={onImageAdded} />
            </div>
        </div>
    )
}

const StyledBackgroundImgRemover = styled(BackgroundImgRemover)((props) => {
    return {
        // backgroundColor: 'white',
        marginTop: -16, // reset global padding-top
        ['.container']: {
            margin: 'auto',
            padding: `${theme.space.large}`,
            maxWidth: 700,
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            zIndex: 0,
            [`${theme.media.largeScreenBreakpoint}`]: {
                padding: `${theme.space.largest} 0`
            },
            ['.display']: { marginBottom: theme.space.larger },
            ['.hero-text']: {
                fontFamily: 'Inter, sans-serif',
                fontSize: 30,
                fontWeight: 700,
                lineHeight: '29px',
                [`${theme.media.largeScreenBreakpoint}`]: {
                    lineHeight: '58px',
                    fontSize: 48
                }
            }
        }
    }
})
export default StyledBackgroundImgRemover
